import { Link } from "react-router-dom";
import { allTopics, formatDate, getCategoryName, getUserAvatar, getUserName, getHottestTopics } from "@/utils/community";
import { siteStats } from "@/mocks/tags";

export default function LatestTopics() {
  const latest = [...allTopics].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 6);
  const hot = getHottestTopics(5);

  return (
    <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 lg:gap-10">
        {/* Latest list */}
        <div>
          <div className="flex items-end justify-between gap-3 mb-6">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">最新话题</h2>
              <p className="mt-2 text-sm md:text-base text-foreground-600">
                看看社区里的伙伴们刚刚聊了什么
              </p>
            </div>
            <Link
              to="/search"
              className="text-sm font-medium text-primary-700 hover:text-primary-800 inline-flex items-center gap-1 whitespace-nowrap cursor-pointer"
            >
              更多话题
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="space-y-3 md:space-y-4">
            {latest.map((topic) => (
              <article
                key={topic.id}
                className="group p-4 md:p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 transition-colors"
              >
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to={`/topic/${topic.id}`}
                    className="w-full sm:w-40 h-36 sm:h-28 rounded-lg overflow-hidden shrink-0 bg-background-200"
                  >
                    <img
                      src={topic.cover}
                      alt={topic.title}
                      title={`${topic.title} - 香蕉社区话题`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </Link>
                  <div className="min-w-0 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-2 py-0.5 rounded-md bg-primary-100 text-primary-800 text-xs font-medium">
                        {getCategoryName(topic.categoryId)}
                      </span>
                      <span className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-800 text-xs">
                        {topic.type}
                      </span>
                    </div>
                    <Link to={`/topic/${topic.id}`} className="mt-2 cursor-pointer">
                      <h3 className="text-base md:text-lg font-semibold text-foreground-950 group-hover:text-primary-700 transition-colors line-clamp-2 leading-snug">
                        {topic.title}
                      </h3>
                    </Link>
                    <p className="mt-1.5 text-xs md:text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                      {topic.excerpt}
                    </p>
                    <div className="mt-auto pt-3 flex items-center justify-between flex-wrap gap-2">
                      <Link
                        to={`/user/${topic.authorId}`}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <span className="w-6 h-6 rounded-full overflow-hidden bg-background-200 shrink-0">
                          <img
                            src={getUserAvatar(topic.authorId)}
                            alt={getUserName(topic.authorId)}
                            className="w-full h-full object-cover"
                          />
                        </span>
                        <span className="text-xs text-foreground-700 hover:text-primary-700">
                          {getUserName(topic.authorId)}
                        </span>
                      </Link>
                      <div className="flex items-center gap-4 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line" />
                          {topic.views}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-chat-3-line" />
                          {topic.replies}
                        </span>
                        <span className="hidden sm:inline">{formatDate(topic.date)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <div className="p-5 rounded-xl bg-background-50 border border-background-200/70">
            <h3 className="text-base font-semibold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-bar-chart-box-line text-accent-600" />
              社区数据
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {siteStats.map((stat) => (
                <div key={stat.label} className="p-3 rounded-lg bg-background-100">
                  <i className={`${stat.icon} text-accent-600 text-lg`} />
                  <p className="mt-2 text-lg font-bold text-foreground-950">{stat.value}</p>
                  <p className="text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="p-5 rounded-xl bg-background-50 border border-background-200/70">
            <h3 className="text-base font-semibold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-fire-line text-primary-600" />
              热门话题榜
            </h3>
            <ol className="space-y-3">
              {hot.map((topic, i) => (
                <li key={topic.id} className="flex items-start gap-3">
                  <span
                    className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold shrink-0 ${
                      i < 3 ? "bg-primary-500 text-foreground-950" : "bg-background-200 text-foreground-600"
                    }`}
                  >
                    {i + 1}
                  </span>
                  <Link
                    to={`/topic/${topic.id}`}
                    className="text-sm text-foreground-800 hover:text-primary-700 transition-colors line-clamp-2 leading-snug cursor-pointer"
                  >
                    {topic.title}
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </aside>
      </div>
    </section>
  );
}